import { JSX, ReactNode } from 'react';
import { Box, Button } from '@mui/material';

interface InterviewFooterProps {
  primaryLabel?: string;
  secondaryLabel?: string;
  tertiaryLabel?: string;
  onPrimary?: () => void;
  onSecondary?: () => void;
  onTertiary?: () => void;
  primaryDisabled?: boolean;
  secondaryDisabled?: boolean;
  tertiaryDisabled?: boolean;
  primaryTestId?: string;
  secondaryTestId?: string;
  tertiaryTestId?: string;
  primaryStartIcon?: ReactNode;
}

export default function InterviewFooter({
  primaryLabel,
  secondaryLabel,
  tertiaryLabel,
  onPrimary,
  onSecondary,
  onTertiary,
  primaryDisabled = false,
  secondaryDisabled = false,
  tertiaryDisabled = false,
  primaryTestId = 'primary-button',
  secondaryTestId = 'secondary-button',
  tertiaryTestId = 'tertiary-button',
  primaryStartIcon,
}: InterviewFooterProps): JSX.Element {
  return (
    <Box
      sx={{
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: { xs: 'flex-start', md: 'flex-end' },
        alignItems: { xs: 'stretch', md: 'center' },
        gap: { xs: 1.5, md: 2 },
        p: 2,
        height: { xs: 'auto', md: 80 },
      }}
    >
      {/* Primary button — full width on mobile, auto on desktop */}
      {primaryLabel && onPrimary && (
        <Button
          variant="contained"
          color="primary"
          onClick={onPrimary}
          disabled={primaryDisabled}
          data-testid={primaryTestId}
          startIcon={primaryStartIcon}
          sx={{
            height: { xs: 52, md: 44 },
            borderRadius: '12px',
            fontSize: 16,
            fontWeight: 600,
            px: { xs: 0, md: 3 },
            width: { xs: '100%', md: 'auto' },
            order: { xs: 0, md: 1 },
            textTransform: 'none',
          }}
        >
          <span>{primaryLabel}</span>
        </Button>
      )}
      {/* Secondary + Tertiary — 50/50 on mobile, inline siblings on desktop */}
      <Box
        sx={{
          display: { xs: 'flex', md: 'contents' },
          gap: { xs: 1.5, md: 2 },
          order: { xs: 1 },
        }}
      >
        {tertiaryLabel != null && tertiaryLabel !== '' && onTertiary && (
          <Button
            onClick={onTertiary}
            disabled={tertiaryDisabled}
            data-testid={tertiaryTestId}
            sx={{
              height: 44,
              borderRadius: '12px',
              border: '1px solid',
              borderColor: 'grey.300',
              color: 'text.secondary',
              fontSize: 14,
              fontWeight: 500,
              px: { xs: 0, md: 2.5 },
              flex: { xs: 1 },
              width: { md: 'auto' },
              order: { md: -1 },
              textTransform: 'none',
            }}
          >
            <span>{tertiaryLabel}</span>
          </Button>
        )}
        {secondaryLabel && onSecondary && (
          <Button
            onClick={onSecondary}
            disabled={secondaryDisabled}
            data-testid={secondaryTestId}
            sx={{
              height: 44,
              borderRadius: '12px',
              border: '1px solid',
              borderColor: 'grey.300',
              color: 'text.secondary',
              fontSize: 14,
              fontWeight: 500,
              px: { xs: 0, md: 2.5 },
              flex: { xs: 1 },
              width: { md: 'auto' },
              order: { md: 0 },
              textTransform: 'none',
            }}
          >
            <span>{secondaryLabel}</span>
          </Button>
        )}
      </Box>
    </Box>
  );
}
